import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

// ─── Brandostro LogoMark ──────────────────────────────────────────────────────
function LogoMark({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <path d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2z" fill="rgba(26,106,255,0.15)" />
      <path d="M16 6C10.477 6 6 10.477 6 16s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6z" fill="rgba(26,106,255,0.25)" />
      <path d="M16 10a6 6 0 100 12 6 6 0 000-12z" fill="#1A6AFF" />
      <path d="M16 13a3 3 0 100 6 3 3 0 000-6z" fill="#fff" />
    </svg>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-root {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1fr 1fr;
    font-family: 'Inter', sans-serif;
    background: #04070D;
  }

  /* ── LEFT PANEL ── */
  .auth-left {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 48px;
    background: #050C1A;
    overflow: hidden;
  }

  .auth-left::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 60% at 20% 20%, rgba(26,106,255,0.12) 0%, transparent 60%),
      radial-gradient(ellipse 60% 80% at 80% 80%, rgba(6,182,212,0.08) 0%, transparent 60%);
    pointer-events: none;
  }

  .auth-left-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(26,106,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(26,106,255,0.04) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
  }

  .auth-wordmark {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.5);
    position: relative;
    z-index: 1;
  }

  .auth-left-content {
    position: relative;
    z-index: 1;
  }

  .auth-left-eyebrow {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #1A6AFF;
    margin-bottom: 20px;
  }

  .auth-left-headline {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(32px, 3.5vw, 52px);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1.0;
    color: #F2F5FF;
    margin-bottom: 20px;
  }

  .auth-left-headline span {
    background: linear-gradient(135deg, #1A6AFF, #06B6D4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .auth-left-sub {
    font-size: 15px;
    color: rgba(242,245,255,0.4);
    line-height: 1.7;
    max-width: 360px;
    margin-bottom: 40px;
  }

  .auth-phases {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .auth-phase {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    transition: all 0.2s;
  }

  .auth-phase:hover {
    background: rgba(26,106,255,0.08);
    border-color: rgba(26,106,255,0.2);
  }

  .auth-phase-num {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 10px;
    font-weight: 700;
    color: rgba(26,106,255,0.6);
    width: 24px;
    flex-shrink: 0;
  }

  .auth-phase-name {
    font-size: 12px;
    font-weight: 500;
    color: rgba(242,245,255,0.5);
  }

  .auth-phase-dot {
    margin-left: auto;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(26,106,255,0.4);
  }

  .auth-left-footer {
    position: relative;
    z-index: 1;
    font-size: 11px;
    color: rgba(242,245,255,0.2);
    line-height: 1.6;
  }

  /* ── RIGHT PANEL ── */
  .auth-right {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
    background: #F2F5FF;
  }

  .auth-card {
    width: 100%;
    max-width: 420px;
  }

  .auth-card-header {
    margin-bottom: 36px;
  }

  .auth-card-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #0A0A1A;
    margin-bottom: 8px;
  }

  .auth-card-sub {
    font-size: 14px;
    color: #6B7280;
    line-height: 1.6;
  }

  .auth-card-sub a {
    color: #1A6AFF;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
  }

  .auth-card-sub a:hover { text-decoration: underline; }

  /* Google button */
  .auth-google-btn {
    width: 100%;
    height: 48px;
    background: #FFFFFF;
    border: 1.5px solid #E2E8F0;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #0A0A1A;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }

  .auth-google-btn:hover {
    border-color: #CBD5E1;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    transform: translateY(-1px);
  }

  .auth-google-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  /* Divider */
  .auth-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }

  .auth-divider-line {
    flex: 1;
    height: 1px;
    background: #E2E8F0;
  }

  .auth-divider-text {
    font-size: 11px;
    font-weight: 600;
    color: #9CA3AF;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  /* Form fields */
  .auth-field {
    margin-bottom: 16px;
  }

  .auth-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .auth-input {
    width: 100%;
    height: 48px;
    padding: 0 16px;
    background: #FFFFFF;
    border: 1.5px solid #E2E8F0;
    border-radius: 12px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #0A0A1A;
    outline: none;
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  }

  .auth-input:focus {
    border-color: #1A6AFF;
    box-shadow: 0 0 0 4px rgba(26,106,255,0.1);
  }

  .auth-input::placeholder { color: #CBD5E1; }
  .auth-input.error { border-color: #EF4444; box-shadow: 0 0 0 4px rgba(239,68,68,0.08); }

  /* Submit button */
  .auth-submit-btn {
    width: 100%;
    height: 50px;
    background: linear-gradient(135deg, #094BCC, #1A6AFF);
    border: none;
    border-radius: 12px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 0.02em;
    box-shadow: 0 4px 16px rgba(26,106,255,0.35);
    margin-top: 8px;
  }

  .auth-submit-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(26,106,255,0.4);
  }

  .auth-submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  /* Error / success messages */
  .auth-error {
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 13px;
    color: #DC2626;
    margin-bottom: 16px;
    line-height: 1.5;
  }

  .auth-success {
    background: #F0FDF4;
    border: 1px solid #BBF7D0;
    border-radius: 10px;
    padding: 16px;
    font-size: 13px;
    color: #15803D;
    margin-bottom: 16px;
    line-height: 1.6;
    text-align: center;
  }

  /* Password field wrapper */
  .auth-pwd-wrap {
    position: relative;
  }

  .auth-pwd-toggle {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    font-size: 13px;
    color: #9CA3AF;
    padding: 4px;
  }

  /* Forgot password */
  .auth-forgot {
    text-align: right;
    margin-top: -8px;
    margin-bottom: 16px;
  }

  .auth-forgot button {
    background: none;
    border: none;
    font-size: 12px;
    color: #1A6AFF;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
  }

  /* Terms */
  .auth-terms {
    font-size: 11px;
    color: #9CA3AF;
    text-align: center;
    margin-top: 16px;
    line-height: 1.6;
  }

  .auth-terms a {
    color: #6B7280;
    text-decoration: underline;
    cursor: pointer;
  }

  /* Verify screen */
  .auth-verify {
    text-align: center;
    padding: 20px 0;
  }

  .auth-verify-icon {
    font-size: 52px;
    margin-bottom: 20px;
    display: block;
  }

  /* Loading spinner */
  .auth-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  /* Mobile */
  @media (max-width: 768px) {
    .auth-root { grid-template-columns: 1fr; }
    .auth-left { display: none; }
    .auth-right { padding: 32px 24px; background: #F2F5FF; min-height: 100vh; align-items: flex-start; padding-top: 60px; }
    .auth-card { max-width: 100%; }
  }
`;

// ─── Phases list for left panel ───────────────────────────────────────────────
const PHASES = [
  { num: '00', name: 'Nomenclature' },
  { num: '01', name: 'Brand DNA' },
  { num: '02', name: 'Company Identity' },
  { num: '03', name: 'Visual Architecture' },
  { num: '04', name: 'Brand Messaging' },
  { num: '05', name: 'Strategic Positioning' },
  { num: '06', name: 'Customer Experience' },
  { num: '07', name: 'Digital Presence' },
  { num: '08', name: 'Go-To-Market' },
  { num: '09', name: 'Launch & Activation' },
];

// ─── Google Icon ──────────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.548 0 9s.348 2.825.957 4.039l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" />
    </svg>
  );
}

// ─── Left Panel ───────────────────────────────────────────────────────────────
function AuthLeft() {
  return (
    <div className="auth-left">
      <div className="auth-left-grid" />
      <div className="auth-wordmark">
        <img src="/assets/monogram.png" alt="Brandostro" width="26" height="26" style={{ borderRadius:'7px', flexShrink:0 }} />
        Brandostro
      </div>
      <div className="auth-left-content">
        <div className="auth-left-eyebrow">Brand Intelligence Platform</div>
        <div className="auth-left-headline">
          Your brand.<br />
          <span>Built in seconds.</span><br />
          Built to last.
        </div>
        <div className="auth-left-sub">
          The brand intelligence platform built for founders who move fast
          and think big. Your complete brand system in under 2 minutes.
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
          {[
            { num:'10', label:'Brand phases', desc:'From naming to launch activation' },
            { num:'01', label:'Sentence needed', desc:'That\'s all it takes to start' },
            { num:'<2', label:'Minutes to completion', desc:'Full professional brand system' },
          ].map((s,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'20px', padding:'16px 20px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px' }}>
              <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'28px', fontWeight:'800', color:'#1A6AFF', letterSpacing:'-0.04em', minWidth:'52px' }}>{s.num}</div>
              <div>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'rgba(242,245,255,0.8)', marginBottom:'2px' }}>{s.label}</div>
                <div style={{ fontSize:'12px', color:'rgba(242,245,255,0.35)' }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="auth-left-footer">
        © {new Date().getFullYear()} Brandostro · The Definitive Brand Intelligence Platform<br />
        brandostro.com · info@brandostro.com
      </div>
    </div>
  );
}

// ─── MAIN AUTH COMPONENT ──────────────────────────────────────────────────────
export default function Auth({ onAuthSuccess }) {
  const [view, setView]       = useState('login'); // login | signup | forgot | verify
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Clear messages on view change
  useEffect(() => {
    setError('');
    setSuccess('');
    setPassword('');
  }, [view]);

  // ── Google Sign In ──
  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `https://app.brandostro.com`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message || 'Google sign in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  // ── Email Login ──
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.user) onAuthSuccess(data.user);
    } catch (err) {
      setError(err.message === 'Invalid login credentials'
        ? 'Incorrect email or password. Please try again.'
        : err.message || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  // ── Email Signup ──
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { setError('Please enter your full name.'); return; }
    if (!email) { setError('Please enter your email address.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      setView('verify');
    } catch (err) {
      setError(err.message === 'User already registered'
        ? 'An account with this email already exists. Please log in.'
        : err.message || 'Signup failed. Please try again.');
    } finally { setLoading(false); }
  };

  // ── Forgot Password ──
  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      setSuccess(`Password reset email sent to ${email}. Check your inbox.`);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-root">
        <AuthLeft />
        <div className="auth-right">
          <div className="auth-card">

            {/* ── VERIFY EMAIL ── */}
            {view === 'verify' && (
              <div className="auth-verify">
                <span className="auth-verify-icon">✉️</span>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'24px', fontWeight:'700', color:'#0A0A1A', marginBottom:'12px', letterSpacing:'-0.02em' }}>
                  Check your inbox
                </div>
                <div style={{ fontSize:'14px', color:'#6B7280', lineHeight:'1.7', marginBottom:'28px' }}>
                  We've sent a confirmation link to <strong>{email}</strong>.
                  Click the link to activate your account and start building your brand.
                </div>
                <button onClick={() => setView('login')} className="auth-submit-btn" style={{ marginTop:0 }}>
                  Back to Login
                </button>
                <div style={{ marginTop:'16px', fontSize:'12px', color:'#9CA3AF' }}>
                  Didn't receive it? Check your spam folder or{' '}
                  <span style={{ color:'#1A6AFF', cursor:'pointer' }} onClick={handleSignup}>resend</span>.
                </div>
              </div>
            )}

            {/* ── LOGIN ── */}
            {view === 'login' && (
              <>
                <div className="auth-card-header">
                  <div className="auth-card-title">Welcome back</div>
                  <div className="auth-card-sub">
                    New to Brandostro?{' '}
                    <a onClick={() => setView('signup')}>Create a free account</a>
                  </div>
                </div>

                <button className="auth-google-btn" onClick={handleGoogle} disabled={googleLoading}>
                  {googleLoading ? <span className="auth-spinner" style={{ borderColor:'rgba(0,0,0,0.2)', borderTopColor:'#0A0A1A' }} /> : <GoogleIcon />}
                  Continue with Google
                </button>

                <div className="auth-divider">
                  <div className="auth-divider-line" />
                  <div className="auth-divider-text">or</div>
                  <div className="auth-divider-line" />
                </div>

                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleLogin}>
                  <div className="auth-field">
                    <label className="auth-label">Email</label>
                    <input className={`auth-input ${error ? 'error' : ''}`} type="email" placeholder="you@example.com"
                      value={email} onChange={e => { setEmail(e.target.value); setError(''); }} autoComplete="email" />
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">Password</label>
                    <div className="auth-pwd-wrap">
                      <input className={`auth-input ${error ? 'error' : ''}`} type={showPwd ? 'text' : 'password'}
                        placeholder="••••••••" value={password}
                        onChange={e => { setPassword(e.target.value); setError(''); }}
                        autoComplete="current-password" style={{ paddingRight:'44px' }} />
                      <button type="button" className="auth-pwd-toggle" onClick={() => setShowPwd(!showPwd)}>
                        {showPwd ? '🙈' : '👁'}
                      </button>
                    </div>
                  </div>
                  <div className="auth-forgot">
                    <button type="button" onClick={() => setView('forgot')}>Forgot password?</button>
                  </div>
                  <button className="auth-submit-btn" type="submit" disabled={loading}>
                    {loading ? <span className="auth-spinner" /> : 'Sign In →'}
                  </button>
                </form>
              </>
            )}

            {/* ── SIGNUP ── */}
            {view === 'signup' && (
              <>
                <div className="auth-card-header">
                  <div className="auth-card-title">Create your account</div>
                  <div className="auth-card-sub">
                    Already have an account?{' '}
                    <a onClick={() => setView('login')}>Sign in</a>
                  </div>
                </div>

                <button className="auth-google-btn" onClick={handleGoogle} disabled={googleLoading}>
                  {googleLoading ? <span className="auth-spinner" style={{ borderColor:'rgba(0,0,0,0.2)', borderTopColor:'#0A0A1A' }} /> : <GoogleIcon />}
                  Continue with Google
                </button>

                <div className="auth-divider">
                  <div className="auth-divider-line" />
                  <div className="auth-divider-text">or</div>
                  <div className="auth-divider-line" />
                </div>

                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleSignup}>
                  <div className="auth-field">
                    <label className="auth-label">Full Name</label>
                    <input className="auth-input" type="text" placeholder="Your full name"
                      value={fullName} onChange={e => { setFullName(e.target.value); setError(''); }}
                      autoComplete="name" />
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">Email</label>
                    <input className="auth-input" type="email" placeholder="you@example.com"
                      value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
                      autoComplete="email" />
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">Password</label>
                    <div className="auth-pwd-wrap">
                      <input className="auth-input" type={showPwd ? 'text' : 'password'}
                        placeholder="Min. 8 characters" value={password}
                        onChange={e => { setPassword(e.target.value); setError(''); }}
                        autoComplete="new-password" style={{ paddingRight:'44px' }} />
                      <button type="button" className="auth-pwd-toggle" onClick={() => setShowPwd(!showPwd)}>
                        {showPwd ? '🙈' : '👁'}
                      </button>
                    </div>
                  </div>
                  <button className="auth-submit-btn" type="submit" disabled={loading}>
                    {loading ? <span className="auth-spinner" /> : 'Create Account →'}
                  </button>
                </form>
                <div className="auth-terms">
                  By creating an account you agree to our{' '}
                  <a href="https://brandostro.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>{' '}
                  and{' '}
                  <a href="https://brandostro.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                </div>
              </>
            )}

            {/* ── FORGOT PASSWORD ── */}
            {view === 'forgot' && (
              <>
                <div className="auth-card-header">
                  <div className="auth-card-title">Reset your password</div>
                  <div className="auth-card-sub">
                    Enter your email and we'll send a reset link.{' '}
                    <a onClick={() => setView('login')}>Back to login</a>
                  </div>
                </div>

                {error && <div className="auth-error">{error}</div>}
                {success && <div className="auth-success">{success}</div>}

                {!success && (
                  <form onSubmit={handleForgot}>
                    <div className="auth-field">
                      <label className="auth-label">Email</label>
                      <input className="auth-input" type="email" placeholder="you@example.com"
                        value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
                        autoComplete="email" />
                    </div>
                    <button className="auth-submit-btn" type="submit" disabled={loading}>
                      {loading ? <span className="auth-spinner" /> : 'Send Reset Link →'}
                    </button>
                  </form>
                )}

                {success && (
                  <button className="auth-submit-btn" onClick={() => setView('login')}>
                    Back to Login →
                  </button>
                )}
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
